import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import {
  ChoiceSelect,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FilterBar,
  NotPermitted,
  type IDataColumn
} from '@/design';
import { useCan } from '@/domains/auth';
import type { IMetricsFirm, IMetricsFirms, IMetricsFirmWrite } from '../../api/metrics-updates-contracts';
import { metricsUpdatesApi } from '../../api/metrics-updates-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { countOf } from '../../lists';
import { refusalMessage } from '../../refusal';
import { WorkPager, useWorkPaging } from '../lists/work-page/pager';

/**
 * Metrics updates → Firms (SR-315, RNK-07's note): the firms the survey's advisors are at, each
 * with how many and whether it supplies its advisors' figures in the round — "Receives metrics"
 * (they come in the firm's file), "Doesn't receive" (each advisor is asked directly) or not set.
 * A firm is survey-api's: a FINRA id where the advisor's registry answer or Salesforce names one,
 * else the firm's name as the profile holds it — drawn as sent, never merged here.
 *
 * Beside each firm, how many of its advisors' figures came in: for a firm that receives them, whether
 * its file has arrived (Import), since its advisors are on no list of the page.
 *
 * **A status not written in the round is the earlier cycle's**, said "carried from <cycle>"; a
 * choice writes the round's own, and "Not set" clears it. "Mark every firm not set as doesn't
 * receive" is one confirmed write over the firms drawn with no status. `responses.annotate`; the
 * answer is the firms read again, adopted whole. A firm's status moves the lists at survey-api's
 * next read of them.
 */

const REASON_ID = 'metrics-firms-not-permitted';
const NOT_SET = 'not_set';

const CHOICES = [
  { value: 'true', label: 'Receives metrics' },
  { value: 'false', label: 'Doesn’t receive' },
  { value: NOT_SET, label: 'Not set' }
];

function choiceOf(firm: IMetricsFirm): string {
  return firm.receives === null ? NOT_SET : String(firm.receives);
}

export function MetricsFirmsTab({
  survey,
  reread,
  onWritten
}: {
  survey: string;
  /** Moved by the page's *Refresh*: the firms are read again. */
  reread: number;
  onWritten: () => void;
}) {
  const can = useCan();
  const permitted = can('responses.annotate');
  const firms = useAdminData((signal) => metricsUpdatesApi.firms(survey, signal), [survey, reread]);
  const [drawn, setDrawn] = React.useState<Nullable<IMetricsFirms>>(null);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [typed, setTyped] = React.useState('');
  // The firms are read whole; the footer pages them here, in the URL's window, a search starting
  // at the first.
  const paging = useWorkPaging({ replace: true });
  const { page, perPage, pages } = paging;

  React.useEffect(() => setDrawn(firms.data), [firms.data]);

  const write = async (items: IMetricsFirmWrite[], sentence: string) => {
    setBusy(true);
    setSaid(null);
    try {
      const answer = await metricsUpdatesApi.setFirms(survey, items);
      setDrawn(answer);
      setSaid(
        answer.pending
          ? `${sentence} Its advisors move between the lists in the background — within a minute or two.`
          : sentence
      );
      // A firm's status moves its advisors between the lists and the stage's counts.
      onWritten();
    } catch (error) {
      setSaid(refusalMessage(error, 'The firms’ status could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  const all = drawn?.firms ?? [];
  const shown = q === '' ? all : all.filter((firm) => firm.label.toLowerCase().includes(q.toLowerCase()));
  const unset = all.filter((firm) => firm.receives === null);
  const describedBy = permitted ? undefined : REASON_ID;

  const columns: IDataColumn<IMetricsFirm>[] = [
    { id: 'firm', header: 'Firm', cell: (firm) => <span data-firm-key={firm.firmKey}>{firm.label}</span> },
    { id: 'advisors', header: 'Advisors', numeric: true, cell: (firm) => firm.advisors },
    {
      id: 'received',
      header: 'Figures received',
      numeric: true,
      // For a firm that sends its advisors' figures, whether its file has come in: X of N.
      cell: (firm) =>
        firm.received === undefined ? '' : (
          <span data-firm-received={firm.received}>
            {firm.received} of {firm.advisors}
          </span>
        )
    },
    {
      id: 'status',
      header: 'Supplies its advisors’ figures',
      cell: (firm) => (
        <span className="grid gap-1">
          <ChoiceSelect
            aria-label={`${firm.label}: status`}
            value={choiceOf(firm)}
            disabled={!permitted || busy}
            onChange={(value) =>
              void write(
                [{ firmKey: firm.firmKey, label: firm.label, receives: value === NOT_SET ? null : value === 'true' }],
                `${firm.label}: ${CHOICES.find((choice) => choice.value === value)?.label ?? value}.`
              )
            }
            choices={CHOICES}
            className="w-48"
          />
          {firm.carriedFrom && (
            <span className="text-[0.75rem] text-muted-foreground" data-carried-from={firm.carriedFrom}>
              Carried from {firm.carriedFrom}
            </span>
          )}
        </span>
      )
    },
    {
      id: 'set',
      header: 'Set',
      cell: (firm) =>
        firm.updatedAt && !firm.carriedFrom ? formatDay(firm.updatedAt.slice(0, 10)) : ''
    }
  ];

  return (
    <div className="grid gap-3" data-metrics-firms>
      <p className="text-sm text-muted-foreground">
        A firm that receives its advisors’ metrics sends them in its own file (Import); its advisors
        are not asked one by one. Everyone at a firm that doesn’t receive them is asked directly.
      </p>
      {firms.error && !drawn && <LoadError message={firms.error} />}
      <FilterBar
        search={{
          label: 'Search firms',
          placeholder: 'Firm name',
          draft: typed,
          onDraft: setTyped,
          onSubmit: () => {
            setQ(typed.trim());
            paging.setPage(1);
          }
        }}
        count={drawn ? countOf(all.length, 'firm') : undefined}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!permitted || busy || unset.length === 0}
          aria-describedby={describedBy}
          onClick={() => setConfirming(true)}
        >
          Mark every firm not set as doesn’t receive…
        </Button>
        {!permitted && <NotPermitted id={REASON_ID} what="setting a firm’s status" />}
      </div>
      {said && (
        <p role="status" className="text-sm text-[var(--color-teal)]" data-firms-said>
          {said}
        </p>
      )}
      <DataTable
        label="Firms"
        columns={columns}
        rows={drawn ? shown.slice((page - 1) * perPage, (page - 1 + pages) * perPage) : null}
        rowKey={(firm) => firm.firmKey}
        state={
          firms.error && !drawn
            ? { kind: 'error', content: firms.error }
            : drawn
              ? { kind: 'empty', content: q ? 'No firm matches.' : 'No advisor of this survey names a firm.' }
              : { kind: 'loading' }
        }
      />
      {drawn && (
        <WorkPager {...paging.pager} total={shown.length} />
      )}
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark {countOf(unset.length, 'firm')} as doesn’t receive?</DialogTitle>
            <DialogDescription>
              Every firm with no status in this round — none set here and none carried from an earlier
              cycle — is marked “Doesn’t receive”, so its advisors are asked directly.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                void write(
                  unset.map((firm) => ({ firmKey: firm.firmKey, label: firm.label, receives: false })),
                  `${countOf(unset.length, 'firm')} marked as doesn’t receive.`
                );
              }}
            >
              Mark them
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
